import { IntegrationSettingsActions } from "./IntegrationSettingsActions";
import CanvasThemeIcon from "../../../images/settings/canvas-theme.svg?react";
import DownloadIcon from "../../../results/components/Resources/download.svg?react";
import { CoreCard, CoreButton, CoreTextBox } from "@verity/components/core";
import { Box, Link, Typography } from "@mui/material";
import { useContext, useRef } from "react";
import { useAsync } from "react-use";
import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import apiClient from "../../../helpers/api-client";
import { formatFileInfo } from "../../../helpers/file";
import { PortalControllerContext } from "../../context";
import GreenCheckCircleIcon from "../../../images/portal/greenCheckCircle.svg?react";
import PurpleExclamationCircleIcon from "../../../images/portal/purpleExclamationCircle.svg?react";

function VisualizerDetectionMessage({ detected }: { detected: boolean | undefined }) {
	if (!detected === undefined) return null;

	return (
		<span style={{ display: "flex", alignItems: "center", gap: "5px", marginLeft: "10px" }}>
			{detected ? <GreenCheckCircleIcon /> : <PurpleExclamationCircleIcon />}
			{detected
				? i18n.t("portal.components.settings.visualizerDownloadTSX.visualizerDetected")
				: i18n.t("portal.components.settings.visualizerDownloadTSX.visualizerUndetected")}
		</span>
	);
}

export function VisualizerDownload({ instituteId, lms }: { instituteId: number; lms: string }) {
	const { visualizerDetected } = useContext(PortalControllerContext);
	const instituteInputRef = useRef<HTMLInputElement>(null);
	const envInputRef = useRef<HTMLInputElement>(null);

	const visualizerInfo = useAsync(async () => {
		if (lms === "canvas" || lms === "desire2learn" || lms === "moodle") {
			try {
				return await apiClient.theme.getVisualizerInfo(lms, instituteId);
			} catch (error) {
				console.error("Failed to fetch visualizer info", error);
			}
		}
	}, [lms, instituteId]);

	let guideLink = "https://support.yuja.com/hc/en-us/p/exam-proctoring";
	let platformName = "";
	let iid = -1;
	let environment = "";
	if (lms === "canvas") {
		platformName = "Canvas";
		guideLink =
			"https://support.yuja.com/hc/en-us/articles/7332584857623-Integrating-YuJa-Verity-for-Test-Proctoring-into-Canvas#h_01HCJH9QWSFKKFBYSG3TX75XM1";
	} else if (lms === "desire2learn") {
		platformName = "D2L";
		guideLink =
			"https://support.yuja.com/hc/en-us/articles/12752624794007-Integrating-YuJa-Verity-for-Test-Proctoring-into-D2L-Brightspace#prevent-students-from-accessing-quizzes-without-yuja-verity-for-test-proctoring-enabled-0-1";
	} else if (lms === "moodle") {
		platformName = "Moodle";
		guideLink =
			"https://support.yuja.com/hc/en-us/articles/8225791775511-Integrating-YuJa-Verity-for-Test-Proctoring-into-Moodle#prevent-students-from-accessing-quizzes-without-yuja-verity-for-test-proctoring-enabled-0-1";
		iid = instituteId;
		if (import.meta.env.MODE === "development" || import.meta.env.MODE === "staging") {
			environment = "Staging";
		} else {
			environment = "Production";
		}
	}
	if (lms !== "canvas" && lms !== "moodle" && lms !== "desire2learn") {
		return <div>{i18n.t("portal.components.settings.visualizerDownloadTSX.lmsUnsupported")}</div>;
	}

	return (
		<div>
			<IntegrationSettingsActions
				actions={[
					{
						text: i18n.t("portal.components.settings.visualizerDownloadTSX.downloadVisualizer", {
							format:
								visualizerInfo?.value?.size && !visualizerInfo.loading
									? formatFileInfo(visualizerInfo.value.size, visualizerInfo.value?.type)
									: "",
						}),
						icon: <DownloadIcon />,
						href: visualizerInfo?.value?.downloadUrl ?? "",
						buttonLoading: visualizerInfo.loading,
					},
				]}
			/>
			<CoreCard
				title={i18n.t("portal.components.settings.visualizerDownloadTSX.lmsVisualizer")}
				rightTitleContent={<VisualizerDetectionMessage detected={visualizerDetected} />}
				icon={CanvasThemeIcon}
				useHeader
				style={{ marginTop: "12px" }}
			>
				<div
					style={{
						color: "#525252",
						fontSize: "15px",
						letterSpacing: "0.1px",
						marginBottom: "10px",
					}}
				>
					<Trans
						i18nKey="portal.components.settings.visualizerDownloadTSX.visualizerText"
						values={{ platformName }}
						components={{ a: <Link href={guideLink} target="_blank" rel="noopener noreferrer" /> }}
						i18n={i18n}
					/>
				</div>
				{visualizerInfo?.value?.viewUrl && (
					<CoreButton
						component="a"
						href={visualizerInfo.value.viewUrl}
						{...{ target: "_blank", rel: "noopener noreferrer" }}
					>
						{i18n.t("portal.components.settings.visualizerDownloadTSX.viewVisualizer")}
					</CoreButton>
				)}
				<Box display="flex" flexDirection="column">
					{iid !== -1 && (
						<Box display="flex" width="100%" justifyContent="space-between">
							<Box display="flex" flexDirection="column">
								<Typography fontWeight={700} fontSize="15px">
									{i18n.t("portal.components.settings.visualizerDownloadTSX.verityInstituteId")}
								</Typography>
								<Typography fontWeight={400} fontSize="14px" lineHeight={1.6}>
									{i18n.t("portal.components.settings.visualizerDownloadTSX.copyInstituteId")}
								</Typography>
							</Box>
							<CoreTextBox
								value={iid}
								readOnly
								showCopyButton
								width="60%"
								inputRef={instituteInputRef}
								sx={{ marginRight: "20px" }}
							/>
						</Box>
					)}
					{environment !== "" && (
						<Box display="flex" width="100%" justifyContent="space-between">
							<Box display="flex" flexDirection="column">
								<Typography fontWeight={700} fontSize="15px">
									{i18n.t("portal.components.settings.visualizerDownloadTSX.environment")}
								</Typography>
								<Typography fontWeight={400} fontSize="14px" lineHeight={1.6}>
									{i18n.t("portal.components.settings.visualizerDownloadTSX.copyEnvironment")}
								</Typography>
							</Box>
							<CoreTextBox value={environment} readOnly showCopyButton width="60%" inputRef={envInputRef} />
						</Box>
					)}
				</Box>
			</CoreCard>
		</div>
	);
}
